import APIError from "./APIError";
import { RRuleStr } from "src/Schedule";

export type UserID = string & { _tag: "__USER_ID__" };
export type LineID = string & { _tag: "__LINE_ID__" };
export interface IProfile {
  id: UserID;
  name: string;
  username: string;
  lines: LineID[];
  disabled?: boolean;
  deleted?: boolean;
  timeZone?: string;
  sendNewsletter?: boolean;
  version?: string;
  pushToken?: string;
  createdAt: string;
}

export interface IProfileListQuery {
  users?: UserID[];
  lines?: LineID[];
  localTimeMin?: number;
  localTimeMax?: number;
  queryTime?: string;
}

export interface IProfileUpdate {
  id: UserID;
  disabled?: boolean;
}

export interface ILineUpdate {
  id: LineID;
  operatorID?: UserID;
  name?: string;
  description?: string;
  schedule?: string;
  private?: boolean;
  addUsers?: UserID[];
  removeUsers?: UserID[];
}

export interface ILine {
  id: LineID;
  operatorID: UserID;
  operatorUsername: string;
  name: string;
  description: string;
  schedule: RRuleStr;
  private: boolean;
}

export interface IBroadcast {
  id?: string;
  startTime: string;
  query: IProfileListQuery;
  sentAt?: string;
  error?: string;
  from?: string;
  prompt?: string;
  promptText?: string;
}

export interface IPushRequest {
  users: UserID[];
  text: string;
}

type JWTFunc = () => Promise<string | null>;

const DEFAULT_URL = "https://api.dialup.com";

export default class APIClient {
  private getJWT: JWTFunc;
  private baseURL: string;

  constructor(getJWT: JWTFunc, baseURL: string = DEFAULT_URL) {
    this.getJWT = getJWT;
    this.baseURL = baseURL;
  }

  public listProfiles(query?: IProfileListQuery): Promise<IProfile[]> {
    query = query || {};

    return this.do("/profiles/query", {
      body: JSON.stringify(query),
      method: "POST",
    });
  }

  public createBroadcast(cast: IBroadcast): Promise<IBroadcast> {
    return this.do("/broadcasts", {
      body: JSON.stringify(cast),
      method: "POST",
    });
  }

  public createPush(push: IPushRequest): Promise<IBroadcast> {
    return this.do("/pushes", {
      body: JSON.stringify(push),
      method: "POST",
    });
  }

  public updateProfile(update: IProfileUpdate): Promise<IProfile> {
    const mask = [];
    if (update.disabled !== undefined) {
      mask.push("disabled");
    }
    (update as any).mask = mask.join(",");

    return this.do(`/profiles/${update.id}`, {
      body: JSON.stringify(update),
      method: "PATCH",
    });
  }

  public createLine(line: ILine): Promise<ILine> {
    return this.do("/lines", {
      body: JSON.stringify(line),
      method: "POST",
    });
  }

  public deleteLine(id: LineID): Promise<ILine> {
    return this.do("/lines/" + id, {
      method: "DELETE",
    });
  }

  public updateLine(update: ILineUpdate): Promise<ILine> {
    const mask = [];
    if (update.operatorID !== undefined) {
      mask.push("operatorID");
    }
    if (update.addUsers !== undefined) {
      mask.push("addUsers");
    }
    if (update.removeUsers !== undefined) {
      mask.push("removeUsers");
    }
    if (update.name !== undefined) {
      mask.push("name");
    }
    if (update.description !== undefined) {
      mask.push("description");
    }
    if (update.private !== undefined) {
      mask.push("private");
    }
    if (update.schedule !== undefined) {
      mask.push("schedule");
    }
    (update as any).mask = mask.join(",");

    return this.do(`/lines/${update.id}`, {
      body: JSON.stringify(update),
      method: "PATCH",
    });
  }

  public listLines(): Promise<ILine[]> {
    return this.do(`/lines`);
  }

  private async do<T>(path: string, opts: RequestInit = {}): Promise<T> {
    const url = this.baseURL + path;

    const headers = new Headers();
    // headers.set('Content-Type', 'application/json; charset=utf-8');

    const jwt = await this.getJWT();
    if (jwt) {
      headers.set("Authorization", `Bearer ${jwt}`);
    }

    opts.headers = headers;

    const resp = await fetch(url, opts);
    const data = await resp.json();

    if (resp.status < 200 || resp.status >= 300) {
      throw new APIError(data.code, data.message, data.status, path);
    }

    return data;
  }
}



// WEBPACK FOOTER //
// ./src/api/APIClient.ts